import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import BlazeButton from '@blaze-react/button';
import classnames from 'classnames';
import { useRouter } from 'next/router';
import { MdMenu, MdClose } from 'react-icons/md';
import { Link } from '@blaze-cms/nextjs-components';
import MenuContext from './MenuContext';
import BlazeLink from '../BlazeLink';

const Menu = ({
  children,
  collapse,
  modifier,
  mobileMenuModifier,
  mobileMenuChildrenModifier,
  mobileIconAlignment,
  mobileButtonModifier,
  hamburgerIconModifier,
  logoOnMobile,
  logoOnDesktop,
  logoOnMobileUrl,
  logoOnMobileAlt,
  logoOnMobileModifier,
  logoOnDesktopModifier,
  closeIconModifier
}) => {
  const router = useRouter();
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  useEffect(() => {
    const handleRouteChange = () => setShowMobileMenu(false);
    router.events.on('routeChangeStart', handleRouteChange);

    return () => {
      router.events.off('routeChangeStart', handleRouteChange);
    };
  }, [router.events]);

  const shouldDisplayChildren = collapse ? showMobileMenu : true;

  const childModifiers = classnames({
    [mobileMenuChildrenModifier]: shouldDisplayChildren,
    [modifier]: !showMobileMenu
  });

  const isMobileMenuExpanded = showMobileMenu ? `${mobileMenuModifier}` : '';
  const menuWrapperClasses = classnames('menu--wrapper', {
    'menu--wrapper--mobile-open': showMobileMenu,
    'menu--wrapper--mobile-closed': collapse && !showMobileMenu
  });

  return (
    <MenuContext.Provider value={{ showMobileMenu }}>
      <div className={menuWrapperClasses}>
        {collapse && (
          <div className="menu--mobile-wrapper">
            <div
              className={`flex w-screen z-50 justify-${mobileIconAlignment} ${isMobileMenuExpanded}`}>
              <BlazeButton
                className={`menu--mobile-button ${mobileButtonModifier}`}
                onClick={() => setShowMobileMenu(!showMobileMenu)}
                label={showMobileMenu ? 'Close mobile menu' : 'Open mobile menu'}>
                <i>
                  {showMobileMenu ? (
                    <MdClose className={closeIconModifier} />
                  ) : (
                    <MdMenu className={hamburgerIconModifier} />
                  )}
                </i>
              </BlazeButton>
            </div>
          </div>
        )}
        {logoOnMobile && !showMobileMenu && (
          <Link href="/" name="mobile menu">
            <img src={logoOnMobileUrl} alt={logoOnMobileAlt} className={logoOnMobileModifier} />
          </Link>
        )}

        <div className="menu--desktop-wrapper">
          <ul className={childModifiers}>
            {logoOnDesktop && (
              <BlazeLink href="/" className="">
                <img
                  src={logoOnMobileUrl}
                  alt={logoOnMobileAlt}
                  className={logoOnDesktopModifier}
                />
              </BlazeLink>
            )}
            {children}
          </ul>
        </div>
      </div>
    </MenuContext.Provider>
  );
};

Menu.propTypes = {
  collapse: PropTypes.bool.isRequired,
  logoOnMobile: PropTypes.bool.isRequired,
  logoOnDesktop: PropTypes.bool,
  logoOnMobileUrl: PropTypes.string,
  logoOnMobileAlt: PropTypes.string,
  logoOnMobileModifier: PropTypes.string,
  logoOnDesktopModifier: PropTypes.string,
  hamburgerIconModifier: PropTypes.string,
  closeIconModifier: PropTypes.string,
  mobileButtonModifier: PropTypes.string,
  mobileMenuModifier: PropTypes.string,
  mobileMenuChildrenModifier: PropTypes.string,
  modifier: PropTypes.string,
  mobileIconAlignment: PropTypes.string,
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node])
};

Menu.defaultProps = {
  logoOnDesktop: false,
  logoOnMobileUrl: '',
  logoOnMobileAlt: '',
  logoOnMobileModifier: '',
  logoOnDesktopModifier: '',
  hamburgerIconModifier: '',
  closeIconModifier: '',
  mobileButtonModifier: '',
  mobileIconAlignment: '',
  mobileMenuModifier: '',
  mobileMenuChildrenModifier: '',
  modifier: '',
  children: []
};

export default Menu;
